import { useState, useRef, useEffect } from 'react'

interface AutocompleteOption {
  value: string
  label: string
  subtitle?: string
}

interface MultiSelectAutocompleteProps {
  selectedValues: string[]
  onChange: (values: string[]) => void
  options: AutocompleteOption[]
  placeholder?: string
  className?: string
  disabled?: boolean
}

export default function MultiSelectAutocomplete({
  selectedValues = [],
  onChange,
  options,
  placeholder = 'Search and select...',
  className = '',
  disabled = false,
}: MultiSelectAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredOptions, setFilteredOptions] = useState<AutocompleteOption[]>(options)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Filter options when search term or options change
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options.filter(opt => !selectedValues.includes(opt.value)))
    } else {
      const filtered = options
        .filter(opt => !selectedValues.includes(opt.value))
        .filter(
          (option) =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
            option.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
            option.subtitle?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      setFilteredOptions(filtered)
    }
    setHighlightedIndex(0)
  }, [searchTerm, options, selectedValues])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (option: AutocompleteOption) => {
    onChange([...selectedValues, option.value])
    setSearchTerm('')
    inputRef.current?.focus()
  }

  const handleRemove = (value: string) => {
    onChange(selectedValues.filter(v => v !== value))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setIsOpen(true)
      e.preventDefault()
      return
    }

    if (!isOpen) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex((prev) => Math.min(prev + 1, filteredOptions.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex((prev) => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        break
      case 'Backspace':
        if (searchTerm === '' && selectedValues.length > 0) {
          onChange(selectedValues.slice(0, -1))
        }
        break
    }
  }

  const getSelectedLabel = (value: string) => {
    const option = options.find(opt => opt.value === value)
    return option?.label || value
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <div className={`min-h-[42px] border rounded px-3 py-1.5 flex flex-wrap gap-1.5 items-center ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}>
          {selectedValues.map(value => (
            <div
              key={value}
              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm"
            >
              <span>{getSelectedLabel(value)}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(value)}
                  className="hover:text-blue-600"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setIsOpen(true)
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={selectedValues.length === 0 ? placeholder : ''}
            disabled={disabled}
            className="flex-1 min-w-[120px] outline-none bg-transparent disabled:cursor-not-allowed"
          />
        </div>
      </div>

      {isOpen && filteredOptions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.map((option, index) => (
            <div
              key={option.value}
              onClick={() => handleSelect(option)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`px-3 py-2 cursor-pointer ${
                index === highlightedIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
              }`}
            >
              <div className="font-medium text-sm text-gray-900">{option.label}</div>
              {option.subtitle && <div className="text-xs text-gray-500">{option.subtitle}</div>}
            </div>
          ))}
        </div>
      )}

      {isOpen && filteredOptions.length === 0 && searchTerm && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3 text-sm text-gray-500">
          No matches found
        </div>
      )}
    </div>
  )
}
